import React from 'react';
import {
  TrendingUpIcon, TargetIcon, ActivityIcon, ChartBarIcon, BollingerBandsIcon,
  RsiExtremesIcon, MacdIcon, MacdMomentumIcon, VwapIcon, IchimokuCloudIcon,
  LayersIcon, MaCrossoverIcon, ScaleIcon, TrendingDownIcon,
} from './Icons';

/**
 * Single source of truth for per-strategy presentation: the display label, the
 * unique glyph, and the dashboard card-class that carries the strategy's accent
 * color (defined in ActiveTradeGroupsTable.css / StrategyFilterModal.css).
 *
 * Consumers (the trade-group cards, the strategy filter modal, and the creation
 * wizard) MUST read icon/label/cardClass from here rather than re-mapping inline —
 * that inline duplication is what let MACD Reversion/Momentum and MA Crossover/
 * Momentum Swing share a glyph in two places at once. Add a strategy = one entry.
 */
export interface StrategyMeta {
  label: string;
  Icon: React.FC<{ size?: number; className?: string }>;
  /** Card class mirroring the strategy's dashboard accent (and gradient strip). */
  cardClass: string;
}

export const STRATEGY_META: Record<string, StrategyMeta> = {
  REGULAR_LADDER:      { label: 'Regular Ladder',           Icon: ChartBarIcon,        cardClass: 'regular-ladder-card' },
  FUTURE_LADDER:       { label: 'Future Ladder',            Icon: TargetIcon,          cardClass: 'future-ladder-card' },
  MOMENTUM_SWING:      { label: 'Momentum Swing',           Icon: TrendingUpIcon,      cardClass: 'momentum-swing-card' },
  VOLATILITY_LADDER:   { label: 'Volatility Ladder',        Icon: ActivityIcon,        cardClass: 'volatility-ladder-card' },
  BOLLINGER_REVERSION: { label: 'Bollinger Reversion',      Icon: BollingerBandsIcon,  cardClass: 'bollinger-reversion-card' },
  RSI_EXTREMES:        { label: 'RSI Extremes',             Icon: RsiExtremesIcon,     cardClass: 'rsi-extremes-card' },
  MACD_REVERSION:      { label: 'MACD Reversion',           Icon: MacdIcon,            cardClass: 'macd-reversion-card' },
  MACD_MOMENTUM:       { label: 'MACD Momentum',            Icon: MacdMomentumIcon,    cardClass: 'macd-momentum-card' },
  VWAP_REVERSION:      { label: 'VWAP Reversion',           Icon: VwapIcon,            cardClass: 'vwap-reversion-card' },
  ICHIMOKU_CLOUD:      { label: 'Ichimoku Cloud',           Icon: IchimokuCloudIcon,   cardClass: 'ichimoku-cloud-card' },
  PIVOT_POINT_TRADING: { label: 'Pivot Point Trading',      Icon: LayersIcon,          cardClass: 'pivot-point-card' },
  MA_CROSSOVER:        { label: 'MA Crossover',              Icon: MaCrossoverIcon,     cardClass: 'ma-crossover-card' },
  // Own rose/coral accent (support-retest-dip-ladder-card) — reusing pivot-point-card's teal
  // read as near-identical to it (and to MACD Reversion's emerald) in practice; every strategy
  // gets its own identity color in ActiveTradeGroupsTable.css / StrategyFilterModal.css, so this
  // one gets one too rather than sharing. TrendingDownIcon keeps the glyph distinct from Pivot
  // Point's LayersIcon (STRATEGY_META's own anti-duplication rule).
  SUPPORT_RETEST_DIP_LADDER: { label: 'Support-Retest Dip Ladder', Icon: TrendingDownIcon, cardClass: 'support-retest-dip-ladder-card' },
  ORCHESTRATED_LADDER: { label: 'Orchestrated Ladder',       Icon: ScaleIcon,           cardClass: 'orchestrated-ladder-card' },
};

/** Look up a strategy's presentation, falling back to the Regular Ladder identity
 *  for any unknown/legacy type (mirrors the card's historical default). */
export const getStrategyMeta = (type: string | null | undefined): StrategyMeta =>
  (type && STRATEGY_META[type]) || STRATEGY_META.REGULAR_LADDER;
